
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next';
import { getSharedApiClient } from '../../../api/client';
import { Currency, Price, Theatre as TheatreApi } from '../../../typings/api';
import { StaticPathParams } from '../../../typings/staticPaths';
import useTranslation from 'next-translate/useTranslation';
import Layout from '../../../components/Layout';
import styles from '../index.module.scss';
import React, { Fragment, useCallback, useRef, useState } from 'react';
import {
  donateRule,
  createFormSchema,
  setLoadingState,
  validateForm,
  resetForm,
  emailRule,
  Permalink,
  createRedirectIfNeeded,
} from '../../../helpers';
import { useRouter } from 'next/router';
import { useAuth, withForcePlanToLoggedInUnsubs } from '../../../context/authContext';
import Cards from '../../../components/Cards';
import { CardType, Namespace } from 'enums';
import { ProvideStripe } from '../../../context/stripeContext';
import { useStripeCtx } from 'context/stripeContext';
import Head from 'next/head';
import { basicSEO } from 'helpers/jsx';
import { useNotificationContext } from 'context';
import Analytics from 'helpers/Analytics';
import { authorizeUserServerSide } from 'api/firebaseAdmin';
import Theatre from 'models/Theatre';
import { useLang } from 'context/langContext';
import AppLink from '../../../components/AppLink/AppLink';
import LoadingComponent from '../../../components/LoadingComponent';
import StripeDisclaimer from 'components/StripeDisclaimer';

interface Props {
  theatre: TheatreApi;
  donateOptions: Price[];
  countryCode: string;
  canonical: string;
}

const DonateTheater: NextPage<Props> = ({ theatre, donateOptions, countryCode, canonical }) => {
  const { t } = useTranslation(Namespace.Theaters);
  const { createNotification } = useNotificationContext();
  const [value, setValue] = useState(donateOptions[0].value);
  const [customAmount, setCustomAmount] = useState('');

  const formRef = useRef(null);
  const router = useRouter();
  const { user, isAnonymous, isUserLoaded } = useAuth();
  const { locale } = useLang();
  const [selectedCard, setSelectedCard] = useState('');

  const getCurrency = useCallback(() => {
    // fallback to EUR
    if (donateOptions.length === 0) {
      return Currency.Eur;
    } else {
      return donateOptions[0].currency;
    }
  }, [donateOptions]);

  const getMinMaxCurrency = useCallback(() => {
    const curr = getCurrency();
    if (curr === Currency.Czk) {
      return [30, 999999];
    } else if (curr === Currency.Eur) {
      return [1, 100000];
    } else if (curr === Currency.Uah) {
      return [40, 999999];
    } else if (curr === Currency.Pln) {
      return [10, 999999];
    }

    return [0, 0];
  }, [getCurrency]);

  const { cardHasError, createPaymentMethod, confirmCardPayment, stripeError, stripeCard } = useStripeCtx();

  const currency = getCurrency();
  const [donateMin, donateMax] = getMinMaxCurrency();
  const RULE_DONATE_MIN = t('min_donate', { value: donateMin, currency });
  const RULE_DONATE_MAX = t('max_donate', { value: donateMax, currency });
  const RULE_NOT_EMPTY = t('common:form.not_empty');
  const RULE_WRONG_EMAIL = t('common:form.email_format');

  function redirectHome() {
    router.push(Permalink.root(user, locale));
  }

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(parseInt(event.target.value));
    },
    [value],
  );

  const onAmountChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCustomAmount(event.target.value);
    },
    [value],
  );

  const onSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const formSchemaObj = {};
      const validateObject = {};
      if (value === -1) {
        formSchemaObj['custom'] = donateRule(RULE_DONATE_MIN, RULE_DONATE_MAX, donateMin, donateMax);
        validateObject['custom'] = customAmount;
      }

      const form = event.target as HTMLFormElement;

      if (!user) {
        validateObject['email'] = form.email.value;
        formSchemaObj['email'] = emailRule(RULE_WRONG_EMAIL, RULE_NOT_EMPTY);
      }

      const formSchema = createFormSchema(formSchemaObj);
      setLoadingState(formRef.current);

      let valid = true;
      if (value === -1 || !user) {
        valid = validateForm(formSchema, validateObject, formRef.current);
      }
      const amount = value === -1 ? customAmount : value.toString();
      const { currency, value: price } = donateOptions[0];

      if (cardHasError && stripeCard.current && stripeError.current) {
        stripeCard.current.classList.add('error');
        stripeError.current.innerHTML = t('choose-plan:card_number_invalid');
      }

      if (valid && isAnonymous && !cardHasError) {
        const paymentRes = await createPaymentMethod();
        if (paymentRes.error) {
          createNotification({ title: t('donation_fail') });
          resetForm(formRef.current);
        } else {
          const email = form.email.value;
          const res = await getSharedApiClient().paymentDonation({
            payment_method: paymentRes.paymentMethod.id,
            currency,
            amount,
            theatre_id: theatre.id,
            email,
          });
          if (res.status === 'SUCCEEDED') {
            createNotification({ title: t('donation_success') });
            redirectHome();
          } else if (res.status === 'ACTION_REQUIRED') {
            const paymentResp = await confirmCardPayment(
              res.payment_intent_client_secret,
              null,
              paymentRes.paymentMethod.id,
            );
            if (paymentResp.error) {
              stripeCard.current.classList.add('error');
              stripeError.current.innerHTML = paymentResp.error.message;
              resetForm(formRef.current);
            } else {
              Analytics.v2_theatreDonation(theatre.name, currency, price);
              createNotification({ title: t('donation_success') });
              redirectHome();
            }
          } else {
            resetForm(formRef.current);
            createNotification({ title: t('donation_fail') });
          }
        }
      } else if (valid && user && !cardHasError) {
        let payMethod: string;
        console.log('selected card ', selectedCard);
        if (selectedCard !== CardType.NewCard && selectedCard !== '') {
          // user choose saved card, try pay with it
          console.log('set card ', selectedCard);
          payMethod = selectedCard;
        } else {
          if (!cardHasError) {
            const respPay = await createPaymentMethod();
            if (respPay.error) {
              createNotification({ title: t('donation_fail') });
              resetForm(formRef.current);
            } else {
              payMethod = respPay.paymentMethod.id;
            }
          }
        }
        try {
          const res = await getSharedApiClient().paymentDonation({
            payment_method: payMethod,
            currency,
            amount,
            theatre_id: theatre.id,
          });

          if (res.status === 'ACTION_REQUIRED') {
            const paymentResp = await confirmCardPayment(res.payment_intent_client_secret, null, payMethod);
            if (paymentResp.error) {
              stripeCard.current.classList.add('error');
              stripeError.current.innerHTML = paymentResp.error.message;
              resetForm(formRef.current);
            } else {
              createNotification({ title: t('donation_success') });
              redirectHome();
            }
          } else {
            createNotification({ title: t('donation_success') });
            redirectHome();
          }
        } catch (e) {
          createNotification({ title: t('donation_fail') });
          resetForm(formRef.current);
        }
      } else {
        resetForm(formRef.current);
      }
    },
    [
      customAmount,
      formRef,
      cardHasError,
      value,
      user,
      selectedCard,
      createPaymentMethod,
      confirmCardPayment,
      stripeError,
      stripeCard,
    ],
  );

  return (
    <Layout countryCode={countryCode}>
      <Head>
        {basicSEO(
          t('seo:donate_title', { theatre: theatre.name }),
          t('seo:donate_description'),
          undefined,
          undefined,
          true,
          canonical,
        )}
      </Head>
      <main className={styles.donate}>
        <div className={styles.donateWrapper}>
          {!isUserLoaded ? (
            <LoadingComponent spinnerSize="medium" />
          ) : isUserLoaded && !user ? (
            <div className={'donate-temp-message'}>
              <h2>{t('must_login')}</h2>
              <AppLink href={Permalink.login(locale)}>
                <a className={'btn btn-primary'}>{t('common:menu.login')}</a>
              </AppLink>
            </div>
          ) : (
            <>
              <h2>{theatre?.name}</h2>
              <h1>{t('donate_how_much')}</h1>
              <p>{t('donate_note')}</p>
              <form ref={formRef} onSubmit={onSubmit}>
                <div className={['radios', styles.radios].join(' ')}>
                  {donateOptions.map((option, i) => (
                    <Fragment key={i}>
                      <input
                        type="radio"
                        name="amount"
                        value={option.value}
                        onChange={onChange}
                        checked={value === option.value}
                        id={`amount${i}`}
                      />
                      <label className="radio" htmlFor={`amount${i}`}>
                        {option.formatted}
                      </label>
                    </Fragment>
                  ))}
                  <input
                    type="radio"
                    name="amount"
                    value="-1"
                    id="amount"
                    onChange={onChange}
                    className="custom-amount"
                  />
                  <label className="radio" htmlFor="amount">
                    {t('amount_other')}
                  </label>
                  {value === -1 ? (
                    <label>
                      {t('amount_in_currency', { currency: donateOptions[0]?.currency })}
                      <input type="text" id="custom" name="custom" onChange={onAmountChange} autoComplete="off" />
                      <span></span>
                    </label>
                  ) : null}
                </div>
                <Cards setSelected={setSelectedCard} selected={selectedCard} />
                <button type="submit" className="btn btn-primary">
                  {t('button_submit')}
                </button>
                <StripeDisclaimer />
              </form>
            </>
          )}
        </div>
      </main>
    </Layout>
  );
};

 async function _getServerSideProps(
  ctx: GetServerSidePropsContext<StaticPathParams>,
): Promise<GetServerSidePropsResult<Props>> {
  const slug = ctx.params?.slug;
  const { apiClient, locale, countryCode, host, hostLocale } = await authorizeUserServerSide(ctx);

  const redirect = createRedirectIfNeeded(ctx, locale);
  if (redirect) {
    return redirect;
  }

  try {
    const [id, ..._slug] = slug.split('-');
    let res: TheatreApi;

    // old urls without ids
    if (isNaN(+id)) {
      res = await apiClient.getTheatreDetail(_slug.join('-'));
    } else {
      res = await apiClient.getTheatreDetail(+id);
    }

    if (!res.donations_allowed) {
      return {
        redirect: {
          permanent: false,
          destination: Permalink.donate(locale),
        },
      };
    }

    const theatre = Theatre.fromJSON(res);

    const tempTheatre = { ...theatre };
    tempTheatre.name = tempTheatre.names?.find((n) => n.language === hostLocale)?.value || theatre.name;
    const canonical = `https://${host}${Permalink.donateTheatre(tempTheatre, hostLocale)}`;

    const localisedSlug = theatre.localisedSlug(locale);

    if (_slug.join('-') !== localisedSlug) {
      return {
        redirect: {
          permanent: true,
          destination: Permalink.donateTheatre(theatre, locale),
        },
      };
    }

    res.plays = theatre.plays.map((p) => Object.assign({}, p, { theatre: { id: theatre.id, slug: theatre.slug } }));
    const donateOptions = await apiClient.getDonateOptions();

    return {
      props: {
        theatre: res,
        donateOptions,
        countryCode,
        canonical,
      },
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
}

export default withForcePlanToLoggedInUnsubs(ProvideStripe(DonateTheater));


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/donate/[slug]/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  